/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019-20, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

angular.module('bulbApp.assetLibraryModule')
  .service(
    'BulbAssetLibraryOneDriveService',
    [
      '$http',
      '$q',
      'BulbAssetLibraryService',
      'BulbCKEStateService',
      'BulbModalService',
      'Msal',
      'OneDrive',
      'Restangular',
      'SerialSaveService',
      'UnitStateStore',
      'CONFIG',
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      BulbAssetLibraryOneDriveService,
    ],
  )
  .value('OneDrive', window.OneDrive)
  .value('Msal', window.Msal);

function BulbAssetLibraryOneDriveService(
  $http,
  $q,
  BulbAssetLibraryService,
  BulbCKEStateService,
  BulbModalService,
  Msal,
  OneDrive,
  Restangular,
  SerialSaveService,
  UnitStateStore,
  CONFIG,
) {
  const that = this;

  // Variable to be used as callback function.
  let _selectHandler;

  // Rest api call
  const assetlibrary = Restangular.all('assetlibrary');

  // Authentication token for the OneDrive API
  let accessToken = null;

  // OneDrive base url for shared resources
  const ONEDRIVE_SHARE_URL = 'https://api.onedrive.com/v1.0/shares/';

  /**
   * Returns true if the entry mimeType is for OneDrive docs, sheets or slides.
   *
   * @param selectedFile for the asset
   * @returns {boolean} if the entry is for any google asset
   */
  this.isOneDriveAsset = function isOneDriveAsset(selectedFile) {
    return (selectedFile) && selectedFile.mimeType.startsWith('application/vnd.openxmlformats');
  };

  /**
   * Initialize OneDrive Picker and set select handler.
   *
   * @param selectHandler callback function.
   * @param filter the array of file extensions.
   * @param importForAssetLibrary {boolean} if the import from asset library is true or false.
   * @param pageView
   */
  this.initializeOneDrivePicker = function initializeOneDrivePicker(
    selectHandler,
    filter = [],
    importForAssetLibrary = false,
    pageView = false,
  ) {
    if (!OneDrive) {
      // eslint-disable-next-line no-param-reassign
      OneDrive = window.OneDrive;
    }
    _selectHandler = selectHandler;
    const oneDriveOptions = {
      clientId: CONFIG.onedrive.client.id,
      action: pageView ? 'share' : 'query',
      multiSelect: false,
      accountSwitchEnabled: false,
      advanced: {
        navigation: {
          disable: true,
        },
        queryParameters: 'select=id,file',
        redirectUri: CONFIG.onedrive.redirectUri,
        filter: that.generateFilterString(filter),
      },
      success(files) {
        if (importForAssetLibrary && (!files.value[0].file || that.isOneDriveAsset(files.value[0].file))) {
          BulbAssetLibraryService.dialog.showError('assetlibrary_onedrive_file_selection_error');
        } else {
          accessToken = files.accessToken;
          _selectHandler(files.value[0]);
        }
      },
      cancel() {},
      error(error) {
        console.log('OneDrive Picker Error: ', error);
      },
    };
    OneDrive.open(oneDriveOptions);
  };

  /**
   * For uploading selected item
   * @param fileId - id for selected file
   * @param folderId - folder id
   */
  this.importFromOneDrive = function importFromOneDrive(fileId, folderId) {
    return assetlibrary.all('onedrive').all('upload').post('', {
      token: accessToken,
      fileId,
      folderId,
    }).catch(that.errorFn);
  };

  /**
   * for showing error message if upload fails.
   * @param res - error response
   */
  this.errorFn = function errorFn(res) {
    if (res.data && res.data.message) {
      BulbAssetLibraryService.dialog.showError(res.data.message);
    } else {
      const stopBusyIndicator = BulbCKEStateService.stopBusyIndicator.value;
      if (stopBusyIndicator) {
        stopBusyIndicator();
        BulbCKEStateService.stopBusyIndicator.next(null);
      }
      BulbAssetLibraryService.dialog.showError('unknown_error');
    }
  };

  /**
   * Convert the filter string into format supported by OneDrive.
   * @param filterString
   */
  this.generateFilterString = function generateFilterString(filterString) {
    if (filterString.length === 0) return '';
    return `folder,.${filterString.join(',').split(',').join(',.')}`;
  };

  /**
   * Method to check whether current fileType is supported or not.
   * @param fileType - File object to verify.
   */
  function supportedFileTypes(fileType) {
    return (that.isOneDriveAsset(fileType) || (fileType && fileType.mimeType.startsWith('application/pdf')
      || fileType.mimeType.startsWith('image/')));
  }

  /**
   * Call the api to get embed url for document.
   * @param url - url of document.
   */
  this.getSharedEmbedUrl = function getSharedEmbedUrl(url) {
    const deferred = $q.defer();
    assetlibrary.all('onedrive').all('generateEmbedUrl').post('', {
      documentUrl: url,
    }).then((response) => {
      if (response.error) {
        deferred.reject('embed_onedrive_url_access_denied');
      } else {
        const downloadUrl = response['@content.downloadUrl'];
        if (!downloadUrl || !supportedFileTypes(response.file)) {
          deferred.reject('embed_onedrive_file_not_supported');
        } else {
          deferred.resolve(downloadUrl);
        }
      }
    })
      .catch((error) => {
        console.log('Error ', error);
        deferred.reject(error);
      });
    return deferred.promise;
  };

  /**
   * Method for creating html embed url.
   * @param url - url of document.
   */
  this.createSharableLinkForDocument = function createSharableLinkForDocument(url) {
    // If user copy the document url from browser, then we need to generate
    // shared url on behalf of user before embedding it in page. And for this, we need to authenticate user,
    // to get the accessToken.
    const deferred = $q.defer();
    that.authenticateUser().then((accessTokenResponse) => {
      assetlibrary.all('onedrive').all('generateSharedUrl').post('', {
        url,
        token: accessTokenResponse?.accessToken,
      }).then((response) => {
        const downloadUrl = response['@content.downloadUrl'];
        if (!downloadUrl || !supportedFileTypes(response.file)) {
          deferred.reject('embed_onedrive_file_not_supported');
        } else {
          deferred.resolve(downloadUrl);
        }
      })
        .catch(((error) => {
          console.log('Error', error);
          deferred.reject(error);
        }));
    });
    return deferred.promise;
  };

  /**
   * Method to authenticate onedrive user and generate accessToken for accessing OneDrive Api.
   * Using MSAL.js (Microsoft Authentication Library).
   */
  this.authenticateUser = function authenticateUser() {
    if (!Msal) {
      // eslint-disable-next-line no-param-reassign
      Msal = window.Msal;
    }
    const msalConfig = {
      auth: {
        clientId: CONFIG.onedrive.client.id,
        redirectUri: CONFIG.onedrive.redirectUri,
      },
    };
    const scopes = {
      scopes: ['onedrive.readwrite'],
    };

    const msalInstance = new Msal.UserAgentApplication(msalConfig);
    if (msalInstance.getAccount()) {
      return msalInstance.acquireTokenPopup(scopes);
    }
    return msalInstance.loginPopup(scopes).then(() => msalInstance.acquireTokenPopup(scopes), (error) => {
      console.log(`Login Error ${error}`);
    });
  };

  /**
   * Method to show one drive error modal.
   * @param modalOptions
   */
  this.showEmbedError = function showEmbedError(modalOptions) {
    BulbAssetLibraryService.dialog.closeCurrentModal();
    return BulbModalService.show(modalOptions);
  };

  /**
   * Method to get the OneDrive item content using shareId.
   * @param shareId - shared id of item
   */
  this.getItemContentByShareId = function (shareId) {
    return `${ONEDRIVE_SHARE_URL + shareId}/root/content`;
  };

  /**
   * Method to generate OneDrive item url using shareId.
   * @param iFrame - existing iFrame element.
   * @param shareId - shared id of one drive item.
   * @param unitId - Unit id
   * @param contentBlockId - content block id
   * @param contentBlockParentDiv - Content block parent div.
   */
  this.updateOneDriveItemUrl = function updateOneDriveItemUrl(
    iFrame,
    shareId,
    unitId,
    contentBlockId,
    contentBlockParentDiv,
  ) {
    $http({
      method: 'GET',
      url: `${ONEDRIVE_SHARE_URL + shareId}/driveItem`,
    }).then((response) => {
      // Extract the token from iframe.
      const currentUrlToken = ((decodeURIComponent(iFrame.src).split('src=')[1]).split('com/')[1]).split('/')[0];
      // Latest url for the item.
      const updatedItemUrl = response.data['@content.downloadUrl'];
      // Extract the token from the latest url.
      const newUrlToken = (updatedItemUrl.split('com/')[1]).split('/')[0];
      // if existing url token is not equal to url token coming in the response url, we need to update the
      // existing frame src and send the patch request to update in the db as well.
      // if currentUrlToken and newUrlToken are equal then no action is required.
      if (currentUrlToken !== newUrlToken) {
        // find the existing url in the iframe which need to be replaced with the new updated one.
        const urlToReplace = unescape(contentBlockParentDiv.split('?src=')[1]).split('\"')[0];
        // Request object with updated html.
        const patch = {
          html: contentBlockParentDiv.replace(encodeURIComponent(urlToReplace), encodeURIComponent(updatedItemUrl)),
        };

        // Sending updated frame url.
        SerialSaveService.save({
          save: () => Restangular.one('units', unitId).one('embedblock', contentBlockId).patch(patch),
        }).then(() => {
          // Update share status to reflect that the page has been edited:
          const ShareTransition = UnitStateStore.getShareTransitionEnum();
          UnitStateStore.transitionShareStatus(ShareTransition.EDIT);
        });

        // Reload the iframe with the new url.
        iFrame.src = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(updatedItemUrl)}`;
      }
    }, (error) => {
      console.log('Error', error);
    });
  };
}
